import { useCallback, useEffect, useRef, useState } from 'react';

import { SimSettingsFormValues } from '@features/Mobile/types';
import { MobileTariff, Sim } from '@generated/gql/graphql';
import { MobileDisplayState } from '@src/types/mobile';

import { useSettingsFormState } from './useSettingsFormState';
import { useSimSubmit } from './useSimSubmit';

const useMobileForm = (
  selectedSims: Sim[],
  availableTariffs: MobileTariff[],
  setHasFormChanged: (hasFormChanged: boolean) => void
) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [displayState, setDisplayState] = useState<MobileDisplayState>(
    MobileDisplayState.simSettings
  );
  const { editedSim, initialValues, mobileTariffs, setEditedSim } =
    useSettingsFormState(selectedSims, availableTariffs);
  const { submitSim } = useSimSubmit(mobileTariffs);

  const hasSelectedSims = selectedSims.length > 0;

  useEffect(() => {
    if (hasSelectedSims) {
      setDisplayState(MobileDisplayState.chosenSims);
    }
  }, [hasSelectedSims]);

  useEffect(() => {
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [displayState]);

  const handleEditSim = useCallback(
    (id: string) => {
      setEditedSim(id);
      setDisplayState(MobileDisplayState.simSettings);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDisplayState]
  );

  const handleSimSubmit = (values: SimSettingsFormValues) => {
    submitSim({
      simId: values.simPlanId,
      prevSimId: editedSim,
      simInput: values,
      onComplete: () => {
        setDisplayState(MobileDisplayState.chosenSims);
        setEditedSim(null);
        setHasFormChanged(true);
      },
    });
  };

  return {
    formRef,
    mobileTariffs,
    initialValues,
    handleSimSubmit,
    handleEditSim,
    setEditedSim,
    setDisplayState,
  };
};

export { useMobileForm };
